/**
 * @fileoverview Favourites API helper for Typesense V2
 * 
 * Fetches favourite products from Typesense by product IDs.
 * Uses the Typesense SDK and returns products in the same format
 * as the Typesense adapter for consistent rendering.
 * 
 * @module FavouritesAPIV2
 */

(function() {
  'use strict';
  
  /**
   * Favourites API helper class for Typesense.
   * Fetches products by Shopify product ID via Typesense.
   * 
   * @class
   */
  class FavouritesAPIV2 {
    /**
     * Fetch products by IDs from Typesense.
     * 
     * @param {string[]} productIds - Array of numeric Shopify product IDs
     * @returns {Promise<Product[]>} Array of products in standard format
     * 
     * @example
     * const products = await FavouritesAPIV2.fetchProducts(['123', '456']);
     */
    static async fetchFavouritesCount(productId) {
      if (!productId) return 0;

      const config = this._getTypesenseConfig();
      if (!config) return 0;

      try {
        const client = this._createClient(config);
        if (!client) return 0;

        const response = await client
          .collections(config.collection)
          .documents()
          .search({
            q: '*',
            query_by: 'title',
            filter_by: `shopify_product_id:=[${productId}]`,
            include_fields: 'favourites_count',
            per_page: 1,
            page: 1,
            use_cache: false
          });

        const count = response.hits?.[0]?.document?.favourites_count;
        const parsed = Number(count);
        return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
      } catch (error) {
        console.error('[FavouritesAPIV2] fetchFavouritesCount error:', error);
        return 0;
      }
    }

    static async fetchProducts(productIds) {
      if (!productIds || productIds.length === 0) {
        return [];
      }

      return this._fetchProducts({
        filterBy: `shopify_product_id:=[${productIds.join(',')}]`,
        orderKeys: productIds.map((id) => String(id)),
        orderField: 'shopifyProductId'
      });
    }

    static async fetchProductsBySku(skus) {
      if (!skus || skus.length === 0) {
        return [];
      }

      const normalizedSkus = skus.map((sku) => String(sku));
      return this._fetchProducts({
        filterBy: `id:=[${normalizedSkus.join(',')}]`,
        orderKeys: normalizedSkus,
        orderField: 'id'
      });
    }

    static async _fetchProducts({ filterBy, orderKeys, orderField }) {
      const config = this._getTypesenseConfig();
      if (!config) {
        return [];
      }

      try {
        const client = this._createClient(config);
        if (!client) {
          console.error('[FavouritesAPIV2] Failed to create Typesense client');
          return [];
        }

        const response = await client
          .collections(config.collection)
          .documents()
          .search({
            q: '*',
            query_by: 'title',
            filter_by: filterBy,
            per_page: 50,
            page: 1,
            use_cache: true
          });

        if (!response.hits || response.hits.length === 0) {
          console.warn('[FavouritesAPIV2] No products found in Typesense');
          return [];
        }

        const products = response.hits.map((hit) => this._transformProduct(hit.document));
        const productsByKey = new Map();
        products.forEach((product) => {
          productsByKey.set(String(product[orderField]), product);
        });

        const orderedProducts = [];
        orderKeys.forEach((key) => {
          const product = productsByKey.get(String(key));
          if (product) {
            orderedProducts.push(product);
          }
        });

        return orderedProducts;
      } catch (error) {
        console.error('[FavouritesAPIV2] Fetch error:', error);
        return [];
      }
    }
    
    static _getTypesenseConfig() {
      if (!window.COLLECTION_V2_CONFIG) {
        console.error('[FavouritesAPIV2] COLLECTION_V2_CONFIG not found');
        return null;
      }

      const config = window.COLLECTION_V2_CONFIG.typesense;

      if (!config?.apiKey || !config?.endpoint || !config?.collection) {
        console.error('[FavouritesAPIV2] Missing Typesense configuration');
        return null;
      }

      return config;
    }

    /**
     * Create Typesense client.
     * 
     * @private
     * @param {Object} config - Typesense configuration
     * @returns {Object|null} Typesense client instance
     */
    static _createClient(config) {
      if (typeof Typesense === 'undefined') {
        console.error('[FavouritesAPIV2] Typesense SDK not loaded');
        return null;
      }
      
      const endpoint = config.endpoint || '';
      const url = new URL(endpoint.startsWith('http') ? endpoint : `https://${endpoint}`);
      
      return new Typesense.Client({
        nodes: [{
          host: url.hostname,
          port: url.port ? parseInt(url.port, 10) : (url.protocol === 'https:' ? 443 : 80),
          protocol: url.protocol.replace(':', '')
        }],
        apiKey: config.apiKey,
        connectionTimeoutSeconds: 5
      });
    }
    
    /**
     * Transform product from Typesense format to standard format.
     * Matches the format returned by TypesenseAPIAdapterV2.
     * 
     * @private
     * @param {Object} doc - Product document from Typesense
     * @returns {Product} Standardized product object
     */
    static _transformProduct(doc) {
      return {
        id: doc.id,
        shopifyProductId: doc.shopify_product_id,
        handle: doc.handle,
        title: doc.title,
        vendor: doc.brand || '',
        price: {
          amount: (doc.price / 100).toFixed(2),
          currencyCode: doc.currency || 'GBP'
        },
        compareAtPrice: doc.compare_at_price ? {
          amount: (doc.compare_at_price / 100).toFixed(2),
          currencyCode: doc.currency || 'GBP'
        } : null,
        availableForSale: doc.available_for_sale,
        featuredImage: {
          url: doc.featured_image_url || '',
          altText: doc.featured_image_alt || doc.title,
          width: 800,
          height: 800
        },
        tags: [],
        createdAt: new Date(doc.created_at * 1000).toISOString(),
        url: doc.url,
        sizeData: doc.size_group_value_product_card
          ? { value: doc.size_group_value_product_card }
          : null,
        condition: doc.condition ? { value: doc.condition } : null,
        favouritesCount: doc.favourites_count ?? 0
      };
    }
  }
  
  // Export to global scope
  window.FavouritesAPIV2 = FavouritesAPIV2;
})();
